import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { getArticleById, articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = getArticleById(id || '');

  if (!article) {
    return (
      <main className="pt-20 pb-16 min-h-screen flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-unknow-line text-3xl" style={{ color: 'oklch(var(--foreground-300))' }}></i>
          </div>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'oklch(var(--foreground-950))' }}>文章未找到</h2>
          <Link to="/" className="text-sm" style={{ color: 'oklch(var(--primary-500))' }}>返回首页</Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = (article.relatedArticles || [])
    .map((rid) => articles.find((a) => a.id === rid))
    .filter(Boolean);

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <main className="pt-20 pb-16" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-8 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:underline cursor-pointer" style={{ color: 'oklch(var(--foreground-500))' }}>首页</Link>
          <span style={{ color: 'oklch(var(--foreground-400))' }}>/</span>
          <Link
            to={`/category/${article.categorySlug}`}
            className="hover:underline cursor-pointer"
            style={{ color: 'oklch(var(--foreground-500))' }}
          >
            {category?.name || '分类'}
          </Link>
          <span style={{ color: 'oklch(var(--foreground-400))' }}>/</span>
          <span className="font-medium truncate max-w-[200px]" style={{ color: 'oklch(var(--foreground-950))' }}>
            {article.title}
          </span>
        </nav>

        {/* Article Header */}
        <header className="mb-10">
          <h1 className="text-2xl md:text-3xl font-heading mb-4 leading-snug" style={{ color: 'oklch(var(--foreground-950))' }}>
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs" style={{ color: 'oklch(var(--foreground-500))' }}>
            <span>作者：{article.author}</span>
            <span>发布于 {article.publishDate}</span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-eye-line text-xs"></i>
              </div>
              {article.readCount.toLocaleString()} 次阅读
            </span>
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: 'oklch(var(--primary-100))', color: 'oklch(var(--primary-700))' }}
              >
                {category.name}
              </Link>
            )}
          </div>
        </header>

        {/* Featured Image */}
        <div className="rounded-lg overflow-hidden mb-10 h-64 md:h-96">
          <img
            src={article.detailImage || ''}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            width={1200}
            height={600}
          />
        </div>

        {/* Article Content */}
        <article className="prose-custom max-w-none mb-12">
          <div className="text-base leading-relaxed" style={{ color: 'oklch(var(--foreground-800))' }}>
            {paragraphs.map((paragraph, i) => {
              const trimmed = paragraph.trim();

              if (/^(第一步|第二步|第三步|第四步|第五步|第六步)/.test(trimmed) || /^\d+\./.test(trimmed)) {
                return (
                  <div key={i} className="mb-4 pl-4 border-l-2 py-1" style={{ borderColor: 'oklch(var(--primary-300))' }}>
                    <p style={{ color: 'oklch(var(--foreground-800))' }}>{trimmed}</p>
                  </div>
                );
              }

              if (/^(Q\d+：)/.test(trimmed)) {
                return (
                  <div key={i} className="mb-4">
                    <p className="font-bold mb-1" style={{ color: 'oklch(var(--foreground-950))' }}>{trimmed.split('：')[0]}：</p>
                    <p style={{ color: 'oklch(var(--foreground-800))' }}>{trimmed.split('：').slice(1).join('：')}</p>
                  </div>
                );
              }

              if (/^(A\d*[：:])/.test(trimmed)) {
                return (
                  <div key={i} className="mb-4 ml-4 p-3 rounded" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
                    <p style={{ color: 'oklch(var(--foreground-800))' }}>{trimmed.replace(/^A\d*[：:]\s*/, '')}</p>
                  </div>
                );
              }

              if (trimmed.length < 80 && trimmed.length > 5) {
                return (
                  <h4 key={i} className="text-lg font-bold mt-8 mb-3" style={{ color: 'oklch(var(--foreground-950))' }}>
                    <a id={`section-${i}`}></a>
                    {trimmed}
                  </h4>
                );
              }

              return (
                <p key={i} className="mb-4 leading-relaxed" style={{ color: 'oklch(var(--foreground-800))' }}>
                  {trimmed}
                </p>
              );
            })}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="border-t pt-8" style={{ borderColor: 'oklch(var(--background-200))' }}>
            <h3 className="text-lg font-bold mb-5" style={{ color: 'oklch(var(--foreground-950))' }}>相关文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedArticles.map((ra) =>
                ra ? (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="p-4 rounded-lg cursor-pointer transition-all duration-200"
                    style={{ backgroundColor: 'oklch(var(--background-100))' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <h4 className="text-sm font-bold mb-1 line-clamp-2" style={{ color: 'oklch(var(--foreground-950))' }}>
                      {ra.title}
                    </h4>
                    <p className="text-xs line-clamp-2" style={{ color: 'oklch(var(--foreground-600))' }}>
                      {ra.summary}
                    </p>
                  </Link>
                ) : null
              )}
            </div>
          </section>
        )}
      </div>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            url: `https://chuniao.app/article/${article.id}`,
            publisher: {
              '@type': 'Organization',
              name: '雏鸟科技',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://chuniao.app',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category?.name || '分类',
                item: `https://chuniao.app/category/${article.categorySlug}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `https://chuniao.app/article/${article.id}`,
              },
            ],
          }),
        }}
      />
    </main>
  );
}